import { useId, type ReactNode } from 'react'
import { Box, CloseButton, Flex, type BoxProps, type CloseButtonProps } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { ModalLayer, type ModalLayerProps } from './ModalLayer'

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'

// Chakra's dialog and drawer recipe widths, so migrated modals keep their size.
const DIALOG_MAX_W: Record<ModalSize, string> = { xs: 'sm', sm: 'md', md: 'lg', lg: '2xl', xl: '4xl', full: '100dvw' }
const DRAWER_MAX_W: Record<ModalSize, string> = { xs: 'xs', sm: 'md', md: 'lg', lg: '2xl', xl: '4xl', full: '100vw' }

export type ModalProps = Omit<ModalLayerProps, 'aria-label' | 'aria-labelledby' | 'title'> & {
  title: ReactNode
  footer?: ReactNode
  size?: ModalSize
  showCloseButton?: boolean
  closeButtonProps?: Omit<CloseButtonProps, 'onClick'>
  headerProps?: BoxProps
  bodyProps?: BoxProps
  footerProps?: BoxProps
}

/** The standard header/body/footer modal card. For bespoke overlays use ModalLayer directly. */
export function Modal({
  title,
  footer,
  size = 'md',
  placement = 'top',
  showCloseButton = true,
  closeButtonProps,
  headerProps,
  bodyProps,
  footerProps,
  children,
  onClose,
  ...layerProps
}: ModalProps) {
  const { t } = useTranslation()
  const titleId = useId()
  const drawer = placement === 'end'
  const fullHeight = drawer || size === 'full'

  return (
    <ModalLayer
      aria-labelledby={titleId}
      onClose={onClose}
      placement={placement}
      display="flex"
      flexDirection="column"
      w="full"
      maxW={(drawer ? DRAWER_MAX_W : DIALOG_MAX_W)[size]}
      h={fullHeight ? '100dvh' : undefined}
      maxH={fullHeight ? '100dvh' : 'calc(100dvh - 8rem)'}
      overflow="hidden"
      borderRadius={fullHeight ? 0 : 'l3'}
      bg="bg.panel"
      boxShadow="lg"
      textStyle="sm"
      animationName={drawer ? 'slide-from-right-full, fade-in' : 'scale-in, fade-in'}
      animationDuration={drawer ? 'slow' : 'moderate'}
      {...layerProps}
    >
      {/* @ts-expect-error Chakra v3: BoxProps.direction clashes with Flex's flexDirection alias */}
      <Flex flex="none" gap={2} px={6} pt={6} pb={4} pe={showCloseButton ? 14 : 6} {...headerProps}>
        <Box id={titleId} flex={1} minW={0} textStyle="lg" fontWeight="semibold">
          {title}
        </Box>
      </Flex>
      {showCloseButton && (
        <CloseButton
          size="sm"
          position="absolute"
          top={2}
          insetEnd={2}
          aria-label={t('common.close')}
          {...closeButtonProps}
          onClick={onClose}
        />
      )}
      <Box flex={1} minH={0} overflowY="auto" px={6} pt={2} pb={6} {...bodyProps}>
        {children}
      </Box>
      {footer && (
        // @ts-expect-error Chakra v3: BoxProps.direction clashes with Flex's flexDirection alias
        <Flex flex="none" align="center" justify="flex-end" gap={3} px={6} pt={2} pb={4} {...footerProps}>
          {footer}
        </Flex>
      )}
    </ModalLayer>
  )
}
